import React from 'react';

import Marquee from '../../../components/blocks/Marquee';

// "By the numbers" was abstract — these are concrete voices. Six Indian
// farmer / FPO-admin testimonials, six international buyer testimonials,
// mixed across four vertical marquee columns rotated in 3D space.

const TESTIMONIALS = [
  // ── Sellers (FPOs and farmers in India) ────────────────────────────────
  {
    name: 'Ramesh Patil',
    role: 'FPO admin · Nashik',
    side: 'seller',
    flag: '🇮🇳',
    body: "Mandi prices used to crash the second we showed up with 20 tonnes. Buyers seeing the stock before they see us changed everything.",
  },
  {
    name: 'Lakshmi Devi',
    role: 'FPO admin · Anantapur',
    side: 'seller',
    flag: '🇮🇳',
    body: "Five years of paperwork compressed into one onboarding evening.",
  },
  {
    name: 'Anil Kumar',
    role: 'Farmer · Sangrur, Punjab',
    side: 'seller',
    flag: '🇮🇳',
    body: "Buyers see our farm now, not a broker's word.",
  },
  {
    name: 'Sunita Rao',
    role: 'FPO admin · Kolar',
    side: 'seller',
    flag: '🇮🇳',
    body: "Counter-offer feature saved our co-op ₹40,000 on one capsicum shipment.",
  },
  {
    name: 'Madhav Singh',
    role: 'Farmer · Meerut',
    side: 'seller',
    flag: '🇮🇳',
    body: "My granddaughter manages our listings from her phone. We list before sunrise.",
  },
  {
    name: 'Rajesh Naik',
    role: 'FPO admin · Sinnar',
    side: 'seller',
    flag: '🇮🇳',
    body: "Pooled stock from eight villages. One invoice goes out, one payment comes in.",
  },

  // ── Buyers (bulk procurement, worldwide) ───────────────────────────────
  {
    name: 'James Anderson',
    role: 'Procurement · Singapore',
    side: 'buyer',
    flag: '🇸🇬',
    body: "Replaced four middlemen with one app.",
  },
  {
    name: 'Sophie Laurent',
    role: 'Sourcing · Paris',
    side: 'buyer',
    flag: '🇫🇷',
    body: "Traceability our European certifier finally accepts without a fight.",
  },
  {
    name: 'Hiroshi Tanaka',
    role: 'Importer · Osaka',
    side: 'buyer',
    flag: '🇯🇵',
    body: "Bulk Grade-A produce, on the dock by Tuesday morning.",
  },
  {
    name: 'Emma Williams',
    role: 'Buyer · Manchester',
    side: 'buyer',
    flag: '🇬🇧',
    body: "I chat with the actual farmer now, not a forwarder.",
  },
  {
    name: 'Carlos Mendez',
    role: 'Wholesaler · Madrid',
    side: 'buyer',
    flag: '🇪🇸',
    body: "Half of our spice procurement runs through Atharva now.",
  },
  {
    name: 'David Chen',
    role: 'Director · Singapore',
    side: 'buyer',
    flag: '🇸🇬',
    body: "Provenance pays for itself. Premium brands demand it.",
  },
];

// Stable colour per name so the avatar is deterministic across renders.
const AVATAR_COLOURS = [
  ['bg-emerald-700', 'text-emerald-50'],
  ['bg-amber-700', 'text-amber-50'],
  ['bg-stone-700', 'text-stone-50'],
  ['bg-rose-800', 'text-rose-50'],
  ['bg-indigo-800', 'text-indigo-50'],
  ['bg-teal-800', 'text-teal-50'],
];

function hashName(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function Avatar({ name }) {
  const [bg, fg] = AVATAR_COLOURS[hashName(name) % AVATAR_COLOURS.length];
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${bg} ${fg}`}>
      {initials}
    </div>
  );
}

function TestimonialCard({ t }) {
  const isSeller = t.side === 'seller';
  return (
    <figure
      className={`w-64 shrink-0 rounded-lg border p-4 backdrop-blur-sm ${
        isSeller
          ? 'border-green-600/40 bg-green-950/55 text-green-50'
          : 'border-amber-500/30 bg-stone-900/60 text-stone-50'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <Avatar name={t.name} />
        <div className="min-w-0 flex-1">
          <figcaption className="flex items-center gap-1 truncate text-sm font-semibold">
            <span className="truncate">{t.name}</span>
            <span className="text-xs">{t.flag}</span>
          </figcaption>
          <p className="truncate text-[11px] text-current/70">{t.role}</p>
        </div>
      </div>
      <blockquote className="mt-3 text-[13px] leading-relaxed text-current/90">
        “{t.body}”
      </blockquote>
    </figure>
  );
}

// Each column gets a different shuffled slice so the wall feels mixed.
// Sellers and buyers alternate within each column so users always see both
// sides moving past at the same time. Six base columns + two xl-only extras
// so wide screens fill cleanly without dead corners.
const COL_A = [TESTIMONIALS[0], TESTIMONIALS[6], TESTIMONIALS[1], TESTIMONIALS[7]];
const COL_B = [TESTIMONIALS[8], TESTIMONIALS[2], TESTIMONIALS[9], TESTIMONIALS[3]];
const COL_C = [TESTIMONIALS[4], TESTIMONIALS[10], TESTIMONIALS[5], TESTIMONIALS[11]];
const COL_D = [TESTIMONIALS[7], TESTIMONIALS[0], TESTIMONIALS[11], TESTIMONIALS[4]];
const COL_E = [TESTIMONIALS[2], TESTIMONIALS[8], TESTIMONIALS[5], TESTIMONIALS[10]];
const COL_F = [TESTIMONIALS[9], TESTIMONIALS[3], TESTIMONIALS[6], TESTIMONIALS[1]];

export default function ImpactSection() {
  return (
    <section
      id="impact"
      className="relative isolate overflow-hidden bg-green-800 py-20 text-white md:py-28"
      style={{ contain: 'paint' }}
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-green-200">Voices on both sides</p>
          <h2 className="mt-4 font-serif text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
            From the village. From the warehouse.
          </h2>
          <p className="mt-5 font-serif text-base italic leading-relaxed text-green-50/90 sm:text-lg">
            The farmers who list and the buyers who source — in their own words.
          </p>
        </div>
      </div>

      {/* 3D wall — full-viewport-wide, rotated perspective so the columns
          recede into the background, framed by gradient fade-outs on every
          edge. Six columns at wide breakpoints, four at narrow. */}
      <div
        className="relative mt-12 flex h-[40rem] w-full items-center justify-center [perspective:400px]"
        style={{
          // clipPath is more reliable than overflow:hidden for 3D-transformed
          // children. `contain: strict` forces the browser to treat this as
          // an isolated paint/layout boundary so cards can't bleed into
          // adjacent sections (the "tear" bug on long scrolls).
          clipPath: 'inset(0)',
          contain: 'strict',
          height: '40rem',
        }}
      >
        <div
          className="flex flex-row items-center gap-4"
          style={{
            // scale(1.45) overshoots the container so the rotated wall fully
            // covers the area — without it, rotateZ(20°) leaves an empty
            // triangle in the upper-left corner. clipPath on the parent
            // clips the excess cleanly even in 3D space.
            transform:
              'translateZ(-100px) rotateX(18deg) rotateY(-8deg) rotateZ(15deg) scale(1.45)',
            transformStyle: 'flat',
            willChange: 'transform',
          }}
        >
          <Marquee vertical pauseOnHover repeat={3} className="[--duration:50s]">
            {COL_A.map((t, i) => <TestimonialCard key={`a${i}`} t={t} />)}
          </Marquee>
          <Marquee vertical pauseOnHover reverse repeat={3} className="[--duration:55s]">
            {COL_B.map((t, i) => <TestimonialCard key={`b${i}`} t={t} />)}
          </Marquee>
          <Marquee vertical pauseOnHover repeat={3} className="[--duration:45s]">
            {COL_C.map((t, i) => <TestimonialCard key={`c${i}`} t={t} />)}
          </Marquee>
          <Marquee vertical pauseOnHover reverse repeat={3} className="[--duration:60s]">
            {COL_D.map((t, i) => <TestimonialCard key={`d${i}`} t={t} />)}
          </Marquee>
          <Marquee vertical pauseOnHover repeat={3} className="[--duration:52s]">
            {COL_E.map((t, i) => <TestimonialCard key={`e${i}`} t={t} />)}
          </Marquee>
          <Marquee vertical pauseOnHover reverse repeat={3} className="[--duration:48s]">
            {COL_F.map((t, i) => <TestimonialCard key={`f${i}`} t={t} />)}
          </Marquee>
          {/* Two extra columns visible only on xl+ screens so wide viewports
              don't end up with a half-empty wall on the right side. */}
          <div className="hidden xl:contents">
            <Marquee vertical pauseOnHover repeat={3} className="[--duration:54s]">
              {COL_A.map((t, i) => <TestimonialCard key={`g${i}`} t={t} />)}
            </Marquee>
            <Marquee vertical pauseOnHover reverse repeat={3} className="[--duration:58s]">
              {COL_C.map((t, i) => <TestimonialCard key={`h${i}`} t={t} />)}
            </Marquee>
          </div>
        </div>

        {/* Edge fades — match the section background. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-green-800 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-green-800 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-green-800 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-green-800 to-transparent" />
      </div>

      {/* Small legend so the colour difference is decodable */}
      <div className="mx-auto mt-8 flex max-w-md items-center justify-center gap-6 px-6 text-xs">
        <span className="flex items-center gap-2 text-green-50/90">
          <span className="h-2 w-2 rounded-full bg-green-300" /> Sellers (FPOs &amp; farmers)
        </span>
        <span className="flex items-center gap-2 text-stone-100/90">
          <span className="h-2 w-2 rounded-full bg-amber-300" /> Buyers (worldwide)
        </span>
      </div>
    </section>
  );
}
